import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import { KeySellingPointData } from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';
import { safeJSONParse } from '../utils';
import { Asset } from '../generated';
import { Observable } from 'rxjs';

@Component({
  template: `
    <vdr-card class="mb-2">
      <vdr-form-field [label]="'Icon'" class="mb-2">
        <vdr-relation-asset-input
          [parentFormControl]="iconControl"
          [config]="config"
          [readonly]="readonly"
        ></vdr-relation-asset-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Title'" class="mb-2">
        <vdr-dynamic-form-input [control]="titleControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>
      <vdr-form-field [label]="'Subtitle'">
        <vdr-dynamic-form-input [control]="valueControl" [def]="textValueDef"></vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class KeySellingPointFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;
  textValueDef: any = { type: 'string', ui: { component: 'text-value-input', args: { activeLanguageCode: this.activeLanguageCode } } };

  keySellingPointData: KeySellingPointData;
  iconControl = new FormControl();
  titleControl = new FormControl();
  valueControl = new FormControl();

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    const defaultKeySellingPointData: KeySellingPointData = {
      iconId: null,
      title: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
      value: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
   };
    this.keySellingPointData = safeJSONParse<KeySellingPointData>(
      this.formControl.value,
      this.notificationService,
      defaultKeySellingPointData,
    )!;
    this.initializeFormControls();
    // Load the Asset based on the stored ID
    if (this.keySellingPointData?.iconId) {
      this.dataService.product
        .getAsset(this.keySellingPointData.iconId.toString())
        .mapSingle((data) => data.asset)
        .subscribe((asset) => {
          this.iconControl.setValue(asset);
        });
    }
  }

  ngAfterViewInit() {
    this.iconControl.valueChanges.subscribe(this.onIconChange);
    this.titleControl.valueChanges.subscribe(this.onTitleChange);
    this.valueControl.valueChanges.subscribe(this.onValueChange);
  }

  private initializeFormControls() {
    // iconControl is set after loading the Asset
    this.titleControl.setValue(this.toTextValueJson(this.keySellingPointData?.title));
    this.valueControl.setValue(this.toTextValueJson(this.keySellingPointData?.value));
  }

  private onIconChange = (value: Asset | null) => {
    const iconId = value ? value.id : null;
    if (this.keySellingPointData?.iconId !== iconId) {
      this.keySellingPointData!.iconId = iconId;
      this.updateFormControlValue();
    }
  };

  private onTitleChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.keySellingPointData?.title) !== JSON.stringify(next)) {
      this.keySellingPointData!.title = next;
      this.updateFormControlValue();
    }
  };

  private onValueChange = (value: string) => {
    const next = this.parseTextValue(value);
    if (JSON.stringify(this.keySellingPointData?.value) !== JSON.stringify(next)) {
      this.keySellingPointData!.value = next;
      this.updateFormControlValue();
    }
  };

  private toTextValueJson(raw: any): string {
    if (raw && typeof raw === 'object' && raw.i18n) return JSON.stringify(raw);
    return JSON.stringify({ format: 'plain', i18n: { [this.activeLanguageCode]: raw ?? '' } });
  }

  private parseTextValue(raw: string | null | undefined): any {
    if (!raw) return { format: 'plain', i18n: {} };
    try { return JSON.parse(raw); } catch { return { format: 'plain', i18n: {} }; }
  }

  private updateFormControlValue() {
    this.formControl.setValue(JSON.stringify(this.keySellingPointData));
    this.formControl.markAsDirty();
  }
}
